import { TFunction } from 'i18next';

import { ClientConfiguration, DataRepositoryEntry } from '@Models';
import { SidebarIcons } from '../icons';
import { MenuItem } from '../types';

/** Normalize the legacy dataRepository shape (string | array | undefined) into a typed array. */
export const resolveDataRepositoryEntries = (
  dataRepository: ClientConfiguration['dataRepository'] | undefined,
): DataRepositoryEntry[] => {
  if (!dataRepository) return [];
  if (typeof dataRepository === 'string') return [{ pageId: dataRepository }];
  return dataRepository.filter((entry) => !!entry?.pageId);
};

export const buildDocumentationItems = (
  dataRepository: ClientConfiguration['dataRepository'] | undefined,
  t: TFunction,
): MenuItem[] => {
  const entries = resolveDataRepositoryEntries(dataRepository);
  if (entries.length === 0) return [];

  if (entries.length === 1) {
    const [entry] = entries;
    return [
      {
        key: `documentation-${entry.pageId}`,
        label: entry.label || t('Sidebar.Documentation'),
        icon: SidebarIcons.documentation,
        link: `/documentation/${entry.pageId}`,
        enabled: true,
      },
    ];
  }

  return [
    {
      key: 'documentation',
      label: t('Sidebar.Documentation'),
      icon: SidebarIcons.documentation,
      enabled: true,
      // Each child falls back to "Page N" when the backend didn't ship a label
      // — otherwise every entry would render as the same "Documentation" string
      // and the dropdown would be unreadable.
      submenu: entries.map((entry, index) => ({
        key: `documentation-${entry.pageId}`,
        label: entry.label || t('Sidebar.DocumentationPage', { number: index + 1 }),
        icon: SidebarIcons.documentation,
        link: `/documentation/${entry.pageId}`,
        enabled: true,
      })),
    },
  ];
};

export const buildTrainingVideosItem = (resources: unknown, t: TFunction): MenuItem[] =>
  resources
    ? [
        {
          key: 'training-videos',
          label: t('Sidebar.TrainingVideos'),
          icon: SidebarIcons.trainingVideos,
          type: 'videos',
          enabled: true,
        },
      ]
    : [];
